import { useState, useEffect, useCallback } from 'react';
import SettingsSection from './common/SettingsSection';
import DateRangePicker from '../layout/DateRangePicker';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../../contexts/ThemeContext';
import { auditService, AuditLog } from '../../services/auditService';
import { Link } from 'react-router-dom';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';

export default function AuditLogs() {
  const { theme } = useTheme();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    entityType: '',
    startDate: format(new Date().setDate(new Date().getDate() - 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });

  // State for the currently selected log (for details view)
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const limit = 10;
      const response = await auditService.getAuditLogs({
        limit,
        page,
        entityType: filters.entityType || undefined,
        startDate: filters.startDate ? new Date(filters.startDate).toISOString() : undefined,
        endDate: filters.endDate ? new Date(filters.endDate).toISOString() : undefined
      });
      setLogs(response.logs);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Returns a human‑readable summary of the audit log
  const renderSummary = (row: AuditLog) => {
    const entityLabel = row.entity_type.charAt(0).toUpperCase() + row.entity_type.slice(1);
    const actionMapping: Record<string, string> = {
      CREATE: 'created',
      UPDATE: 'updated',
      DELETE: 'deleted'
    };
    return (
      <span className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
        {entityLabel} #{row.entity_id} was {actionMapping[row.action] || row.action} by{' '}
        {row.changed_by}
      </span>
    );
  };

  // Render the entity ID as a link (preventing propagation so the row click isn’t triggered)
  const renderEntityLink = (row: AuditLog) => {
    let path = '#';
    switch (row.entity_type) {
      case 'ticket':
        path = `/tickets/${row.entity_id}`;
        break;
      case 'comment':
        path = `/comments/${row.entity_id}`;
        break;
      case 'customer':
        path = `/customers/${row.entity_id}`;
        break;
      default:
        break;
    }
    return (
      <Link
        to={path}
        onClick={(e) => e.stopPropagation()}
        className={
          theme === 'dark' ? 'text-blue-400 hover:underline' : 'text-blue-600 hover:underline'
        }
      >
        {row.entity_id}
      </Link>
    );
  };

  // When a row is clicked, set the selected log to show details
  const handleRowClick = (row: AuditLog) => {
    setSelectedLog(row);
  };

  // Render the modal with complete audit log details
  const renderModal = () => {
    if (!selectedLog) return null;
    return (
      <div className="fixed inset-0 z-[100] overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          {/* Background overlay */}
          <div
            className="fixed inset-0 bg-black/80 transition-opacity"
            aria-hidden="true"
            onClick={() => setSelectedLog(null)}
          ></div>

          {/* Modal content */}
          <div
            className={`relative transform overflow-hidden rounded-lg ${
              theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'
            } px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6`}
          >
            <div>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`text-lg font-medium ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}
                >
                  Audit Log Details
                </h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className={`rounded-md p-1 hover:bg-opacity-80 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Date
                    </label>
                    <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
                      {format(parseISO(selectedLog.changed_at), 'MMM d, yyyy HH:mm:ss')}
                    </div>
                  </div>
                  <div>
                    <label
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Entity Type
                    </label>
                    <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
                      {selectedLog.entity_type.charAt(0).toUpperCase() +
                        selectedLog.entity_type.slice(1)}
                    </div>
                  </div>
                  <div>
                    <label
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Entity ID
                    </label>
                    <div>{renderEntityLink(selectedLog)}</div>
                  </div>
                  <div>
                    <label
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Changed By
                    </label>
                    <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
                      {selectedLog.changed_by}
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Summary
                  </label>
                  <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
                    {renderSummary(selectedLog)}
                  </div>
                </div>

                <div>
                  <label
                    className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Changes
                  </label>
                  <div
                    className={`mt-2 p-4 rounded-md font-mono text-xs overflow-auto max-h-80 ${
                      theme === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-gray-50 text-gray-900'
                    }`}
                  >
                    <pre className="whitespace-pre-wrap">
                      {selectedLog.action === 'DELETE'
                        ? JSON.stringify(selectedLog.old_data, null, 2)
                        : selectedLog.action === 'CREATE'
                          ? JSON.stringify(selectedLog.new_data, null, 2)
                          : JSON.stringify(
                              {
                                old: selectedLog.old_data,
                                new: selectedLog.new_data
                              },
                              null,
                              2
                            )}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Define the columns for the compact table (using table-fixed for consistent widths)
  const columns = [
    {
      key: 'changed_at',
      label: 'Date',
      cell: (value: string) => (
        <span className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>
          {format(parseISO(value), 'MMM d, yyyy HH:mm')}
        </span>
      )
    },
    {
      key: 'entity_type',
      label: 'Type',
      cell: (value: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === 'ticket'
              ? theme === 'dark'
                ? 'bg-blue-900/50 text-blue-200'
                : 'bg-blue-100 text-blue-800'
              : value === 'comment'
                ? theme === 'dark'
                  ? 'bg-green-900/50 text-green-200'
                  : 'bg-green-100 text-green-800'
                : theme === 'dark'
                  ? 'bg-orange-900/50 text-orange-200'
                  : 'bg-orange-100 text-orange-800'
          }`}
        >
          {value}
        </span>
      )
    },
    {
      key: 'entity_id',
      label: 'ID',
      cell: (_: any, row: AuditLog) => renderEntityLink(row)
    },
    {
      key: 'action',
      label: 'Action',
      cell: (value: string) => {
        const actionMapping: Record<string, string> = {
          CREATE: 'Created',
          UPDATE: 'Updated',
          DELETE: 'Deleted'
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              value === 'CREATE'
                ? theme === 'dark'
                  ? 'bg-green-900/50 text-green-200'
                  : 'bg-green-100 text-green-800'
                : value === 'UPDATE'
                  ? theme === 'dark'
                    ? 'bg-yellow-900/50 text-yellow-200'
                    : 'bg-yellow-100 text-yellow-800'
                  : theme === 'dark'
                    ? 'bg-red-900/50 text-red-200'
                    : 'bg-red-100 text-red-800'
            }`}
          >
            {actionMapping[value] || value}
          </span>
        );
      }
    },
    {
      key: 'changed_by',
      label: 'Changed By',
      cell: (value: number | string) => (
        <span className={theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}>{value}</span>
      )
    },
    {
      key: 'summary',
      label: 'Summary',
      cell: (_: any, row: AuditLog) => renderSummary(row)
    }
  ];

  return (
    <SettingsSection title="Audit Logs">
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex gap-4 items-end">
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Entity Type
            </label>
            <select
              className={`block w-50 px-4 py-2 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md transition-colors ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
              value={filters.entityType}
              onChange={(e) => setFilters((prev) => ({ ...prev, entityType: e.target.value }))}
              disabled={loading}
            >
              <option value="" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                All Types
              </option>
              <option value="ticket" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                Ticket
              </option>
              <option value="comment" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                Comment
              </option>
              <option value="customer" className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}>
                Customer
              </option>
            </select>
          </div>
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onDateChange={(start, end) =>
              setFilters((prev) => ({ ...prev, startDate: start, endDate: end }))
            }
            disabled={loading}
          />
        </div>

        {/* Table */}
        <div className="mt-4">
          <div
            className={`overflow-x-auto rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}
          >
            <table className="min-w-full table-fixed divide-y divide-gray-200">
              <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                className={`${
                  theme === 'dark'
                    ? 'bg-gray-800 divide-y divide-gray-700'
                    : 'bg-white divide-y divide-gray-200'
                }`}
              >
                {loading ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className={`px-4 py-8 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      Loading audit logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className={`px-4 py-8 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleRowClick(row)}
                    >
                      {columns.map((col) => (
                        <td key={col.key} className="px-4 py-2">
                          {col.cell(row[col.key as keyof AuditLog], row)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center">
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing {logs.length} of {totalCount} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className={`px-3 py-1 rounded-md text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
                } border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(totalCount / 50) || loading}
                className={`px-3 py-1 rounded-md text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
                } border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Render the modal if a row is selected */}
      {renderModal()}
    </SettingsSection>
  );
}
